<template>
    <div class="col-span-12 flex flex-col pb-8 lg:col-span-2">
        <component :is="link.href ? SidebarLink : 'div'"
            v-for="link in normalizedLinks"
            :key="`link-${link.href}-${link.label}`"
            :nested="false"
            :href="link.href"
            @click="link.onClick"
            :active="link.active"
            :pathMatcher="link.pathMatcher"
            :icon="link.icon"
            :appearance="props.appearance">

            <template v-if="link.href">
                {{ link.label }}
            </template>
            <div v-else
                class="mb-6">
                <div class="text-slate-600 text-base font-extrabold font-sans mb-2">
                    {{ link.label }}
                </div>
                <div class="relative" :class="appearanceStyles.sublinks">
                    <sidebar-link v-for="sublink in link.links"
                        :key="`sublink-${sublink.href}-${sublink.label}`"
                        :nested="true"
                        :href="sublink.href"
                        :active="sublink.active"
                        :pathMatcher="sublink.pathMatcher"
                        :icon="sublink.icon"
                        :appearance="props.appearance"
                        @click="sublink.onClick">
                        <span>{{ sublink.label }}</span>
                    </sidebar-link>
                </div>

            </div>
        </component>
    </div>
</template>
<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, h } from 'vue';
import { useRouter, useRoute } from 'vue-router';

const props = defineProps({
    links: {
        required: true,
        type: Array
    },
    bold: {
        type: Boolean,
        required: false,
        default: true,
    },
    appearance: {
        type: String,
        required: false,
    },
});

const router = useRouter();
const route = useRoute();

const appearanceStyles = computed(() => {
    if (props.appearance === 'nested') {
        return {
            nestedLink: 'pl-3 border-solid border-l border-y-0 border-r-0 border-slate-400 text-sm font-normal!',
        }
    }
    return {};
})

const isActive = (href, pathMatcher) => {
    const path = route?.path ?? '';
    const hash = route?.hash ?? '';
    const pathWithHash = path + hash;
    if (pathMatcher) {
        return Boolean(pathMatcher(pathWithHash));
    }
    return href === path || href === pathWithHash || href === route?.fullPath;
}

const hasIcons = computed(() => props.links.some(link => link.icon));
const bold = computed(() => props.bold)

const normalizeLink = (link) => {
    if (!link.to) {
        return link;
    }
    const resolved = router.resolve(link.to);
    return {
        ...link,
        href: resolved.href,
        onClick: (e) => {
            e.preventDefault();
            router.push(link.to);
        },
        active: link.active ?? isActive(resolved.href, link.pathMatcher),
    };
};

const normalizedLinks = computed(() => {
    return props.links.map((link) => {
        const normalized = normalizeLink(link);
        if (Array.isArray(normalized.links)) {
            return { ...normalized, links: normalized.links.map(normalizeLink) };
        }
        return normalized;
    });
});

const SidebarLink = (props, { slots }) => h('a', {
    class: ['group no-underline! py-1 hover:text-sky-600 flex items-center', {
        'font-extrabold': bold.value,
    }, [props.active || isActive(props.href, props.pathMatcher) ? 'text-sky-600':'text-slate-600'], {
        [appearanceStyles.value.nestedLink]: props.nested
    }],
    href: props.href,
}, [
    props.icon
        ? h('div', { class: 'w-[20px] h-[16px] mr-2 inline-flex items-center justify-start' }, [
            h(FontAwesomeIcon, {
                icon: props.icon,
                class: ['group-hover:text-sky-600', {
                    'text-slate-400': !isActive(props.href, props.pathMatcher),
                    'text-sky-600': isActive(props.href, props.pathMatcher),
                }],
            })
        ])
        : (hasIcons.value && h('div', { class: 'inline-block w-[20px] h-[16px] mr-2' })),
    slots.default()
]);
</script>
